// PWA glue: service-worker registration, plus two behaviours that only ever
// affect the installed (standalone) app so normal browser use is untouched:
//
//   1. A floating Back button. In standalone mode there is no browser back
//      button or URL bar, so without this a user who taps into a course lesson
//      has no reliable way back. Shown only when running as an installed app,
//      when there is somewhere to go back to, and when the bottom tab bar is
//      not already on screen (the tabs are the way around the main app).
//
//   2. "Maintain last visit" — remember the last page and, on a cold launch
//      that lands on the manifest start_url, restore it. Standalone-only, and
//      guarded to run once per launch so it can never loop.

const START_PATHS = ['/', '/videos'];
const LAST_VISIT_KEY = 'rafa:last-visit';
// A remembered page older than this is ignored on launch: after a long gap,
// starting fresh beats being teleported to something you've forgotten about.
const LAST_VISIT_TTL_MS = 24 * 60 * 60 * 1000;

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Paths we never want to save/restore as a "last visit": transient auth and
// onboarding flows that should always start from their own entry point.
function isRestorablePath(path) {
  if (!path || !path.startsWith('/')) return false;
  return !/^\/(students\/(sign_in|sign_up|password)|onboarding|users\/)/.test(path);
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {
      // Registration failures are non-fatal — the app works without the SW.
    });
  });
}

// The tab bar is the primary way around the installed app. The floating
// control only earns its place on screens that do not render it (course
// player) or where it is hidden (desktop width). Keyboard dismissal uses
// visibility, not display, so a tab screen stays "visible" while typing.
function tabBarVisible() {
  const bar = document.getElementById('tab_bar');
  if (!bar) return false;
  return getComputedStyle(bar).display !== 'none';
}

function ensureBackButton() {
  if (!isStandalone()) return;
  let button = document.getElementById('pwa-back-button');
  if (!button) {
    button = document.createElement('button');
    button.id = 'pwa-back-button';
    button.type = 'button';
    button.setAttribute('aria-label', 'Go back');
    button.innerHTML =
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M15 18l-6-6 6-6"/></svg>';
    Object.assign(button.style, {
      position: 'fixed',
      left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      zIndex: '40',
      width: '44px',
      height: '44px',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '9999px',
      border: '1px solid rgba(255,255,255,0.14)',
      background: 'rgba(20,22,24,0.72)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#EAE6DF',
      boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
      cursor: 'pointer'
    });
    button.addEventListener('click', () => window.history.back());
    document.body.appendChild(button);
  }
  const show = window.history.length > 1 && !tabBarVisible();
  button.style.display = show ? 'inline-flex' : 'none';
}

function rememberVisit() {
  const path = window.location.pathname + window.location.search;
  if (isRestorablePath(window.location.pathname)) {
    // localStorage (not sessionStorage) so the last page survives the app being
    // fully closed, which is the whole point of restoring it on a cold launch.
    // Every page is saved — including the start pages — so a stale deep link
    // can never outlive a more recent visit; the timestamp enforces the TTL.
    const entry = JSON.stringify({ path, ts: Date.now() });
    try { window.localStorage.setItem(LAST_VISIT_KEY, entry); } catch (e) { /* private mode */ }
  }
}

function readLastVisit() {
  let raw;
  try { raw = window.localStorage.getItem(LAST_VISIT_KEY); } catch (e) { return null; }
  if (!raw) return null;
  let entry;
  try { entry = JSON.parse(raw); } catch (e) { return null; } // pre-TTL plain-string value
  if (!entry || typeof entry.path !== 'string' || typeof entry.ts !== 'number') return null;
  if (Date.now() - entry.ts > LAST_VISIT_TTL_MS) return null;
  return entry.path;
}

// Runs once at boot. If we launched standalone straight onto the start_url and
// we have a fresh remembered page from this device, jump there.
function maybeRestoreLastVisit() {
  if (!isStandalone()) return;
  if (!START_PATHS.includes(window.location.pathname)) return;
  const last = readLastVisit();
  if (last && isRestorablePath(last.split('?')[0]) && last !== window.location.pathname) {
    if (window.Turbo) {
      window.Turbo.visit(last, { action: 'replace' });
    } else {
      window.location.replace(last);
    }
  }
}

registerServiceWorker();
maybeRestoreLastVisit();

document.addEventListener('turbo:load', () => {
  ensureBackButton();
  rememberVisit();
});

// Specs call this directly. A synthetic turbo:load would also run page-view tracking.
window.rafaEnsureBackButton = ensureBackButton;

// Cover the very first paint before Turbo has fired (and non-Turbo layouts).
if (document.readyState !== 'loading') {
  ensureBackButton();
  rememberVisit();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    ensureBackButton();
    rememberVisit();
  });
}
